import '../App.css';
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";
import { REPOS } from "../constants";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const SCHEDULE_EXAMPLES = [
    { label: "Every day at 9am", value: "0 9 * * *" },
    { label: "Every Monday at 8am", value: "0 8 * * 1" },
    { label: "Every hour", value: "0 * * * *" },
    { label: "Every 30 min", value: "*/30 * * * *" },
];

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "20px",
};

function formatDate(d) {
    if (!d) return "—";
    return new Date(d).toLocaleString();
}

const CronJobs = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    const [schedule, setSchedule] = useState("");
    const [prompt, setPrompt] = useState("");
    const [repo, setRepo] = useState(REPOS[0]);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    const getToken = useCallback(async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return authgear.accessToken;
    }, []);

    const fetchJobs = useCallback(async () => {
        try {
            const token = await getToken();
            const { data } = await axios.get(`${base_url}/cron`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setJobs(data);
        } catch (_) {}
        setLoading(false);
    }, [getToken]);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        fetchJobs();
    }, [navigate, fetchJobs]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!name.trim() || !schedule.trim() || !prompt.trim()) return;
        setSubmitting(true);
        setFormError(null);
        try {
            const token = await getToken();
            const { data } = await axios.post(
                `${base_url}/cron`,
                { name: name.trim(), schedule: schedule.trim(), prompt: prompt.trim(), repo },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setJobs(prev => [data, ...prev]);
            setName("");
            setSchedule("");
            setPrompt("");
        } catch (err) {
            setFormError(err.response?.data?.message || err.message);
        }
        setSubmitting(false);
    };

    const handleToggle = async (job) => {
        try {
            const token = await getToken();
            const { data } = await axios.patch(
                `${base_url}/cron/${job._id}`,
                { active: !job.active },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setJobs(prev => prev.map(j => j._id === job._id ? data : j));
        } catch (_) {}
    };

    const handleDelete = async (id) => {
        try {
            const token = await getToken();
            await axios.delete(`${base_url}/cron/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setJobs(prev => prev.filter(j => j._id !== id));
        } catch (_) {}
    };

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 20px", maxWidth: "860px", margin: "0 auto" }}>
                <h2 style={{ color: "#00d4ff", margin: "0 0 24px" }}>Cron Jobs</h2>

                {/* Create form */}
                <div style={card}>
                    <div style={{ fontSize: "11px", color: "#555", marginBottom: "16px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        New Cron Job
                    </div>
                    <form onSubmit={handleCreate}>
                        <div style={{ display: "flex", gap: "12px", marginBottom: "12px", flexWrap: "wrap" }}>
                            <div style={{ flex: "1 1 200px" }}>
                                <label style={{ fontSize: "12px", color: "#888", display: "block", marginBottom: "4px" }}>Name</label>
                                <input
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder="Daily cleanup"
                                    disabled={submitting}
                                    style={{
                                        width: "100%", background: "#0d0d1a", border: "1px solid #2a2a4a",
                                        borderRadius: "6px", color: "#ddd", padding: "8px 10px",
                                        fontSize: "13px", boxSizing: "border-box",
                                    }}
                                />
                            </div>
                            <div style={{ flex: "1 1 180px" }}>
                                <label style={{ fontSize: "12px", color: "#888", display: "block", marginBottom: "4px" }}>
                                    Schedule (cron)
                                </label>
                                <input
                                    value={schedule}
                                    onChange={e => setSchedule(e.target.value)}
                                    placeholder="0 9 * * *"
                                    disabled={submitting}
                                    style={{
                                        width: "100%", background: "#0d0d1a", border: "1px solid #2a2a4a",
                                        borderRadius: "6px", color: "#ddd", padding: "8px 10px",
                                        fontSize: "13px", fontFamily: "monospace", boxSizing: "border-box",
                                    }}
                                />
                                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "6px" }}>
                                    {SCHEDULE_EXAMPLES.map(ex => (
                                        <button
                                            key={ex.value}
                                            type="button"
                                            onClick={() => setSchedule(ex.value)}
                                            style={{
                                                padding: "3px 8px", background: "transparent",
                                                color: "#555", border: "1px solid #2a2a4a",
                                                borderRadius: "4px", cursor: "pointer", fontSize: "11px",
                                            }}
                                        >
                                            {ex.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <label style={{ fontSize: "12px", color: "#888", display: "block", marginBottom: "4px" }}>Repository</label>
                        <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                            {REPOS.map(r => (
                                <button
                                    key={r}
                                    type="button"
                                    onClick={() => setRepo(r)}
                                    disabled={submitting}
                                    style={{
                                        padding: "5px 12px", background: repo === r ? "#2a2a4a" : "transparent",
                                        color: repo === r ? "#00d4ff" : "#555",
                                        border: `1px solid ${repo === r ? "#00d4ff" : "#2a2a4a"}`,
                                        borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontFamily: "monospace",
                                    }}
                                >
                                    {r}
                                </button>
                            ))}
                        </div>

                        <label style={{ fontSize: "12px", color: "#888", display: "block", marginBottom: "4px" }}>Prompt</label>
                        <textarea
                            value={prompt}
                            onChange={e => setPrompt(e.target.value)}
                            disabled={submitting}
                            rows={4}
                            placeholder="e.g. Review recent commits and add missing tests"
                            style={{
                                width: "100%", background: "#0d0d1a", border: "1px solid #2a2a4a",
                                borderRadius: "6px", color: "#ddd", padding: "10px",
                                fontSize: "13px", resize: "vertical", boxSizing: "border-box",
                            }}
                        />

                        {formError && (
                            <div style={{ fontSize: "12px", color: "#f87171", marginTop: "8px" }}>{formError}</div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting || !name.trim() || !schedule.trim() || !prompt.trim()}
                            style={{
                                marginTop: "12px", padding: "8px 20px",
                                background: submitting ? "#2a2a4a" : "#00d4ff",
                                color: submitting ? "#555" : "#0d0d1a",
                                border: "none", borderRadius: "6px",
                                cursor: submitting ? "not-allowed" : "pointer",
                                fontWeight: "bold", fontSize: "13px",
                            }}
                        >
                            {submitting ? "Creating..." : "Create Job"}
                        </button>
                    </form>
                </div>

                {/* Job list */}
                <div style={{ marginTop: "24px" }}>
                    {loading ? (
                        <div style={{ color: "#555", fontSize: "13px" }}>Loading...</div>
                    ) : jobs.length === 0 ? (
                        <div style={{ ...card, color: "#555", fontSize: "13px" }}>No cron jobs yet.</div>
                    ) : (
                        jobs.map(job => (
                            <div key={job._id} style={{ ...card, marginBottom: "12px", opacity: job.active ? 1 : 0.5 }}>
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                                            <span style={{ fontWeight: "bold", color: "#ddd", fontSize: "14px" }}>{job.name}</span>
                                            <span style={{
                                                fontSize: "11px", padding: "2px 8px", borderRadius: "4px",
                                                background: job.active ? "#1a3a2a" : "#2a2a2a",
                                                color: job.active ? "#4ecdc4" : "#555",
                                                border: `1px solid ${job.active ? "#4ecdc4" : "#333"}`,
                                            }}>
                                                {job.active ? "active" : "paused"}
                                            </span>
                                        </div>
                                        <div style={{ fontFamily: "monospace", fontSize: "12px", color: "#a855f7", marginBottom: "6px" }}>
                                            {job.schedule}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>
                                            {job.repo || process.env.GITHUB_REPO}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                                            {job.prompt.length > 120 ? job.prompt.slice(0, 120) + "…" : job.prompt}
                                        </div>
                                        <div style={{ fontSize: "11px", color: "#444", marginTop: "8px" }}>
                                            Last run: {formatDate(job.lastRunAt)}
                                            {job.lastJobId && (
                                                <span style={{ marginLeft: "10px", fontFamily: "monospace" }}>
                                                    job {job.lastJobId.slice(-6)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                                        <button
                                            onClick={() => handleToggle(job)}
                                            style={{
                                                padding: "6px 14px", background: "transparent",
                                                color: job.active ? "#f59e0b" : "#4ecdc4",
                                                border: `1px solid ${job.active ? "#f59e0b" : "#4ecdc4"}`,
                                                borderRadius: "6px", cursor: "pointer", fontSize: "12px",
                                            }}
                                        >
                                            {job.active ? "Pause" : "Resume"}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(job._id)}
                                            style={{
                                                padding: "6px 14px", background: "transparent",
                                                color: "#f87171", border: "1px solid #f87171",
                                                borderRadius: "6px", cursor: "pointer", fontSize: "12px",
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default CronJobs;
